import React, { useEffect, useMemo, useState, Component } from 'react';
import { Plus, Edit2, Trash2, Tag, Percent, DollarSign, Gift, ToggleLeft, ToggleRight, Loader2, Calendar, ShoppingBag, AlertCircle, Search, Filter, Check, Building2, Utensils } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { useToast } from '../components/ui/Toast';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getSupabase } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { getCompanies } from '../utils/companyManagement';
interface Promotion {
  id: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed' | 'bogo' | 'free_item';
  value: number;
  code?: string;
  minOrderAmount: number;
  maxDiscount: number;
  validFrom: string;
  validTo: string;
  active: boolean;
  usageLimit: number;
  usageCount: number;
  applicableCompanies: any[];
  applicableMenuItems: any[];
  createdBy: string;
  createdAt?: string;
}
export function PromotionsPage() {
  useDocumentTitle('Promociones');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Promotion | null>(null);
  const [loading, setLoading] = useState(true);
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todas');
  const [typeFilter, setTypeFilter] = useState('Todos');
  const {
    showToast
  } = useToast();
  const {
    addLog
  } = useActivityLog();
  useEffect(() => {
    loadData();
  }, []);
  // Real-time subscription
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const client = getSupabase();
    if (!client) return;
    const subscription = client.channel('promotions-realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'promotions'
    }, () => {
      loadPromotionsSilent();
    }).subscribe();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPromotions(), loadCompaniesAndMenu()]);
    } finally {
      setLoading(false);
    }
  };
  const loadCompaniesAndMenu = async () => {
    try {
      // Load companies
      if (isSupabaseConfigured()) {
        const dbCompanies = await supabaseClient.getCompanies();
        setCompanies(dbCompanies);
      } else {
        setCompanies(getCompanies());
      }
      // Load menu items
      if (isSupabaseConfigured()) {
        const items = await supabaseClient.getMenuItems();
        setMenuItems(items);
      }
    } catch (error) {
      console.error('Error loading auxiliary data:', error);
    }
  };
  const loadPromotionsSilent = async () => {
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getPromotions();
        setPromotions(data);
      }
    } catch (error) {
      console.error('Error auto-refreshing promotions:', error);
    }
  };
  const loadPromotions = async () => {
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getPromotions();
        setPromotions(data);
      } else {
        const saved = localStorage.getItem('criket_promotions');
        setPromotions(saved ? JSON.parse(saved) : []);
      }
    } catch (error) {
      console.error('Error loading promotions:', error);
      const saved = localStorage.getItem('criket_promotions');
      setPromotions(saved ? JSON.parse(saved) : []);
    }
  };
  const handleSave = async (promotion: Omit<Promotion, 'id' | 'usageCount' | 'createdAt'>) => {
    try {
      if (editingPromotion) {
        if (isSupabaseConfigured()) {
          await supabaseClient.updatePromotion(editingPromotion.id, promotion);
        }
        setPromotions(promotions.map(p => p.id === editingPromotion.id ? {
          ...p,
          ...promotion
        } : p));
        addLog('Promoción Actualizada', promotion.name, 'promotion');
        showToast('success', 'Promoción actualizada', `"${promotion.name}" ha sido actualizada`);
      } else {
        const newPromotion = {
          ...promotion,
          id: `promo-${Date.now()}`,
          usageCount: 0
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.createPromotion(newPromotion);
        }
        setPromotions([newPromotion, ...promotions]);
        addLog('Promoción Creada', promotion.name, 'promotion');
        showToast('success', 'Promoción creada', `"${promotion.name}" ha sido creada`);
      }
      await loadPromotions();
      setShowModal(false);
      setEditingPromotion(null);
    } catch (error) {
      console.error('Error saving promotion:', error);
      showToast('error', 'Error', 'No se pudo guardar la promoción');
    }
  };
  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deletePromotion(deleteConfirm.id);
        }
        setPromotions(promotions.filter(p => p.id !== deleteConfirm.id));
        addLog('Promoción Eliminada', deleteConfirm.name, 'promotion');
        showToast('success', 'Promoción eliminada', `"${deleteConfirm.name}" ha sido eliminada`);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting promotion:', error);
        showToast('error', 'Error', 'No se pudo eliminar la promoción');
      }
    }
  };
  const toggleActive = async (promotion: Promotion) => {
    try {
      const newActive = !promotion.active;
      if (isSupabaseConfigured()) {
        await supabaseClient.updatePromotion(promotion.id, {
          active: newActive
        });
      }
      setPromotions(promotions.map(p => p.id === promotion.id ? {
        ...p,
        active: newActive
      } : p));
      showToast('success', newActive ? 'Promoción activada' : 'Promoción desactivada');
    } catch (error) {
      console.error('Error toggling promotion:', error);
      showToast('error', 'Error', 'No se pudo cambiar el estado');
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Percent size={20} data-id="element-5179" />;
      case 'fixed':
        return <DollarSign size={20} data-id="element-5180" />;
      case 'bogo':
        return <Gift size={20} data-id="element-5181" />;
      case 'free_item':
        return <Gift size={20} data-id="element-5182" />;
      default:
        return <Tag size={20} data-id="element-5183" />;
    }
  };
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'percentage':
        return 'Porcentaje';
      case 'fixed':
        return 'Monto Fijo';
      case 'bogo':
        return 'Compra 1 Lleva 2';
      case 'free_item':
        return 'Artículo Gratis';
      default:
        return type;
    }
  };
  const getStatus = (promotion: Promotion) => {
    const now = new Date();
    const validFrom = new Date(promotion.validFrom);
    const validTo = new Date(promotion.validTo);
    // Adjust validTo to end of day
    validTo.setHours(23, 59, 59, 999);
    if (!promotion.active) return 'inactive';
    if (now < validFrom) return 'scheduled';
    if (now > validTo) return 'expired';
    if (promotion.usageLimit > 0 && promotion.usageCount >= promotion.usageLimit) return 'depleted';
    return 'active';
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800" data-id="element-5184">
            Activa
          </span>;
      case 'inactive':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700" data-id="element-5185">
            Inactiva
          </span>;
      case 'scheduled':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800" data-id="element-5186">
            Programada
          </span>;
      case 'expired':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800" data-id="element-5187">
            Expirada
          </span>;
      case 'depleted':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800" data-id="element-5188">
            Agotada
          </span>;
      default:
        return null;
    }
  };
  const filteredPromotions = useMemo(() => {
    return promotions.filter(p => {
      // Search filter
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.code?.toLowerCase().includes(searchTerm.toLowerCase()) || p.description.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      // Status filter
      const status = getStatus(p);
      if (statusFilter !== 'Todas') {
        if (statusFilter === 'Activas' && status !== 'active') return false;
        if (statusFilter === 'Inactivas' && status !== 'inactive') return false;
        if (statusFilter === 'Expiradas' && status !== 'expired') return false;
        if (statusFilter === 'Programadas' && status !== 'scheduled') return false;
      }
      // Type filter
      if (typeFilter !== 'Todos') {
        const typeMap: Record<string, string> = {
          Porcentaje: 'percentage',
          'Monto Fijo': 'fixed',
          '2x1': 'bogo',
          Gratis: 'free_item'
        };
        if (p.type !== typeMap[typeFilter]) return false;
      }
      return true;
    });
  }, [promotions, searchTerm, statusFilter, typeFilter]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5189">
        <div className="text-center" data-id="element-5190">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto mb-4" data-id="element-5191" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-5192">
            Cargando promociones...
          </p>
        </div>
      </div>;
  }
  return <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-id="element-5193">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5194">
        <div data-id="element-5195">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight mb-2" data-id="element-5196">
            Gestión de Promociones
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg" data-id="element-5197">
            Crea y administra descuentos y ofertas especiales
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-5199" />} onClick={() => {
        setEditingPromotion(null);
        setShowModal(true);
      }} size="lg" className="shadow-lg shadow-gray-900/10" data-id="element-5198">
          Nueva Promoción
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5200">
        <div className="flex-1" data-id="element-5201">
          <SearchBar placeholder="Buscar por nombre, código..." value={searchTerm} onSearch={setSearchTerm} className="w-full" data-id="element-5202" />
        </div>
        <div className="flex gap-3" data-id="element-5203">
          <FilterDropdown label="Estado" value={statusFilter} options={['Todas', 'Activas', 'Inactivas', 'Expiradas', 'Programadas']} onChange={setStatusFilter} className="w-40" data-id="element-5204" />
          <FilterDropdown label="Tipo" value={typeFilter} options={['Todos', 'Porcentaje', 'Monto Fijo', '2x1', 'Gratis']} onChange={setTypeFilter} className="w-40" data-id="element-5205" />
        </div>
      </div>

      {/* Promotions List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5206">
        {filteredPromotions.length === 0 ? <div className="col-span-1 lg:col-span-2 text-center py-24 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-5207">
            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5208">
              <Tag size={32} className="text-gray-400" data-id="element-5209" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5210">
              No se encontraron promociones
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2 mb-6" data-id="element-5211">
              Intenta ajustar los filtros de búsqueda.
            </p>
            {promotions.length === 0 && <Button onClick={() => setShowModal(true)} leftIcon={<Plus size={18} data-id="element-5213" />} data-id="element-5212">
                Crear Primera Promoción
              </Button>}
          </div> : filteredPromotions.map(promotion => {
        const status = getStatus(promotion);
        const usagePercent = promotion.usageLimit > 0 ? Math.min(100, promotion.usageCount / promotion.usageLimit * 100) : 0;
        return <Card key={promotion.id} className="hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-800 group relative overflow-hidden" data-id="element-5214">
                {/* Status Stripe */}
                <div className={`absolute top-0 left-0 w-1.5 h-full ${status === 'active' ? 'bg-green-500' : status === 'expired' ? 'bg-red-500' : status === 'scheduled' ? 'bg-blue-500' : 'bg-gray-300'}`} data-id="element-5215" />

                <div className="pl-3" data-id="element-5216">
                  <div className="flex items-start justify-between mb-4" data-id="element-5217">
                    <div className="flex items-start gap-4" data-id="element-5218">
                      <div className={`p-3 rounded-xl border ${promotion.active ? 'bg-black text-white border-black' : 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800 dark:border-gray-700'}`} data-id="element-5219">
                        {getTypeIcon(promotion.type)}
                      </div>
                      <div data-id="element-5220">
                        <div className="flex items-center gap-2 mb-1" data-id="element-5221">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight" data-id="element-5222">
                            {promotion.name}
                          </h3>
                          {getStatusBadge(status)}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1" data-id="element-5223">
                          {promotion.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-5224">
                      <button onClick={() => toggleActive(promotion)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title={promotion.active ? 'Desactivar' : 'Activar'} data-id="element-5225">
                        {promotion.active ? <ToggleRight className="text-green-600" size={24} data-id="element-5226" /> : <ToggleLeft size={24} data-id="element-5227" />}
                      </button>
                      <button onClick={() => {
                  setEditingPromotion(promotion);
                  setShowModal(true);
                }} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Editar" data-id="element-5228">
                        <Edit2 size={18} data-id="element-5229" />
                      </button>
                      <button onClick={() => setDeleteConfirm(promotion)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Eliminar" data-id="element-5230">
                        <Trash2 size={18} data-id="element-5231" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mb-4 space-y-3 border border-gray-100 dark:border-gray-800" data-id="element-5232">
                    <div className="flex items-center justify-between text-sm" data-id="element-5233">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-5234">
                        Descuento
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-5235">
                        {promotion.type === 'percentage' ? `${promotion.value}%` : `RD$ ${promotion.value}`}
                      </span>
                    </div>

                    {promotion.code && <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-5236">
                        <span className="text-gray-500 dark:text-gray-400" data-id="element-5237">
                          Código
                        </span>
                        <code className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 rounded font-mono text-xs font-bold tracking-wide" data-id="element-5238">
                          {promotion.code}
                        </code>
                      </div>}

                    <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-5239">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-5240">
                        Vigencia
                      </span>
                      <span className="text-gray-900 dark:text-white font-medium text-xs" data-id="element-5241">
                        {new Date(promotion.validFrom).toLocaleDateString()} -{' '}
                        {new Date(promotion.validTo).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Usage Progress */}
                  {promotion.usageLimit > 0 && <div className="mb-2" data-id="element-5242">
                      <div className="flex justify-between text-xs mb-1.5" data-id="element-5243">
                        <span className="text-gray-500" data-id="element-5244">
                          Uso: {promotion.usageCount} / {promotion.usageLimit}
                        </span>
                        <span className="font-bold text-gray-700 dark:text-gray-300" data-id="element-5245">
                          {Math.round(usagePercent)}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-5246">
                        <div className={`h-full rounded-full transition-all duration-500 ${usagePercent >= 100 ? 'bg-red-500' : usagePercent >= 80 ? 'bg-orange-500' : 'bg-blue-500'}`} style={{
                  width: `${usagePercent}%`
                }} data-id="element-5247" />
                      </div>
                    </div>}

                  {/* Restrictions Badges */}
                  <div className="flex flex-wrap gap-2 mt-3" data-id="element-5248">
                    {promotion.applicableCompanies?.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 rounded text-[10px] font-medium border border-purple-100 dark:border-purple-800" data-id="element-5249">
                        <Building2 size={10} data-id="element-5250" />{' '}
                        {promotion.applicableCompanies.length} Empresas
                      </span>}
                    {promotion.applicableMenuItems?.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 rounded text-[10px] font-medium border border-orange-100 dark:border-orange-800" data-id="element-5251">
                        <Utensils size={10} data-id="element-5252" />{' '}
                        {promotion.applicableMenuItems.length} Platos
                      </span>}
                    {promotion.minOrderAmount > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-[10px] font-medium border border-gray-200 dark:border-gray-700" data-id="element-5253">
                        Min: RD$ {promotion.minOrderAmount}
                      </span>}
                  </div>
                </div>
              </Card>;
      })}
      </div>

      {/* Promotion Form Modal */}
      {showModal && <PromotionFormModal promotion={editingPromotion} companies={companies} menuItems={menuItems} onSave={handleSave} onClose={() => {
      setShowModal(false);
      setEditingPromotion(null);
    }} data-id="element-5254" />}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5255">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-5256">
            <div className="p-6 text-center" data-id="element-5257">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5258">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-5259" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-5260">
                ¿Eliminar promoción?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6" data-id="element-5261">
                ¿Estás seguro de que deseas eliminar{' '}
                <strong data-id="element-5262">"{deleteConfirm.name}"</strong>? Esta acción no se puede
                deshacer.
              </p>
              <div className="flex gap-3" data-id="element-5263">
                <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-5264">
                  Cancelar
                </Button>
                <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 transition-colors" data-id="element-5265">
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Promotion Form Modal Component
function PromotionFormModal({
  promotion,
  companies,
  menuItems,
  onSave,
  onClose
}: {
  promotion: Promotion | null;
  companies: any[];
  menuItems: any[];
  onSave: (promotion: Omit<Promotion, 'id' | 'usageCount' | 'createdAt'>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    name: promotion?.name || '',
    description: promotion?.description || '',
    type: (promotion?.type || 'percentage') as any,
    value: promotion?.value || 0,
    code: promotion?.code || '',
    minOrderAmount: promotion?.minOrderAmount || 0,
    maxDiscount: promotion?.maxDiscount || 0,
    validFrom: promotion?.validFrom || new Date().toISOString().split('T')[0],
    validTo: promotion?.validTo || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    active: promotion?.active ?? true,
    usageLimit: promotion?.usageLimit || 0,
    applicableCompanies: promotion?.applicableCompanies || [],
    applicableMenuItems: promotion?.applicableMenuItems || [],
    createdBy: promotion?.createdBy || 'Admin'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  const toggleCompany = (id: number) => {
    const current = formData.applicableCompanies;
    const updated = current.includes(id) ? current.filter(c => c !== id) : [...current, id];
    setFormData({
      ...formData,
      applicableCompanies: updated
    });
  };
  const toggleMenuItem = (id: string) => {
    const current = formData.applicableMenuItems;
    const updated = current.includes(id) ? current.filter(i => i !== id) : [...current, id];
    setFormData({
      ...formData,
      applicableMenuItems: updated
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-id="element-5266">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl border border-gray-200 dark:border-gray-800 my-8 flex flex-col max-h-[90vh]" data-id="element-5267">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-900 z-10 rounded-t-2xl" data-id="element-5268">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5269">
            {promotion ? 'Editar Promoción' : 'Nueva Promoción'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-5270">
            <span className="text-2xl leading-none" data-id="element-5271">&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8 overflow-y-auto flex-1" data-id="element-5272">
          {/* Basic Info */}
          <section className="space-y-4" data-id="element-5273">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2" data-id="element-5274">
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5275">
              <div className="md:col-span-2" data-id="element-5276">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5277">
                  Nombre de la Promoción
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Ej: Descuento de Verano" data-id="element-5278" />
              </div>

              <div className="md:col-span-2" data-id="element-5279">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5280">
                  Descripción
                </label>
                <textarea required rows={2} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none" placeholder="Describe la promoción..." data-id="element-5281" />
              </div>

              <div data-id="element-5282">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5283">
                  Tipo de Promoción
                </label>
                <select value={formData.type} onChange={e => setFormData({
                ...formData,
                type: e.target.value as any
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5284">
                  <option value="percentage" data-id="element-5285">Porcentaje de Descuento</option>
                  <option value="fixed" data-id="element-5286">Monto Fijo</option>
                  <option value="bogo" data-id="element-5287">Compra 1 Lleva 2</option>
                  <option value="free_item" data-id="element-5288">Artículo Gratis</option>
                </select>
              </div>

              <div data-id="element-5289">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5290">
                  Valor {formData.type === 'percentage' ? '(%)' : '(RD$)'}
                </label>
                <input type="number" required min="0" step="0.01" value={formData.value} onChange={e => setFormData({
                ...formData,
                value: parseFloat(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5291" />
              </div>

              <div data-id="element-5292">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5293">
                  Código Promocional (Opcional)
                </label>
                <input type="text" value={formData.code} onChange={e => setFormData({
                ...formData,
                code: e.target.value.toUpperCase()
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono uppercase tracking-wide" placeholder="VERANO2024" data-id="element-5294" />
              </div>
            </div>
          </section>

          {/* Restrictions */}
          <section className="space-y-4" data-id="element-5295">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2" data-id="element-5296">
              Restricciones y Validez
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5297">
              <div data-id="element-5298">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5299">
                  Pedido Mínimo (RD$)
                </label>
                <input type="number" min="0" step="0.01" value={formData.minOrderAmount} onChange={e => setFormData({
                ...formData,
                minOrderAmount: parseFloat(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5300" />
              </div>

              <div data-id="element-5301">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5302">
                  Descuento Máximo (RD$)
                </label>
                <input type="number" min="0" step="0.01" value={formData.maxDiscount} onChange={e => setFormData({
                ...formData,
                maxDiscount: parseFloat(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5303" />
              </div>

              <div data-id="element-5304">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5305">
                  Válido Desde
                </label>
                <input type="date" required value={formData.validFrom} onChange={e => setFormData({
                ...formData,
                validFrom: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5306" />
              </div>

              <div data-id="element-5307">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5308">
                  Válido Hasta
                </label>
                <input type="date" required value={formData.validTo} onChange={e => setFormData({
                ...formData,
                validTo: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5309" />
              </div>

              <div data-id="element-5310">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5311">
                  Límite de Usos (0 = ilimitado)
                </label>
                <input type="number" min="0" value={formData.usageLimit} onChange={e => setFormData({
                ...formData,
                usageLimit: parseInt(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5312" />
              </div>
            </div>
          </section>

          {/* Applicability */}
          <section className="space-y-4" data-id="element-5313">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2" data-id="element-5314">
              Aplicabilidad
            </h3>

            <div data-id="element-5315">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5316">
                Empresas (Selecciona para restringir)
              </label>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-40 overflow-y-auto bg-gray-50 dark:bg-gray-800/50" data-id="element-5317">
                {companies.length === 0 ? <p className="text-sm text-gray-500 text-center py-2" data-id="element-5318">
                    No hay empresas registradas
                  </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-5319">
                    {companies.map(company => <label key={company.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-5320">
                        <input type="checkbox" checked={formData.applicableCompanies.includes(company.id)} onChange={() => toggleCompany(company.id)} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-5321" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-5322">
                          {company.name}
                        </span>
                      </label>)}
                  </div>}
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5323">
                {formData.applicableCompanies.length === 0 ? 'Válido para TODAS las empresas' : `Restringido a ${formData.applicableCompanies.length} empresa(s)`}
              </p>
            </div>

            <div data-id="element-5324">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5325">
                Platos Específicos (Selecciona para restringir)
              </label>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-40 overflow-y-auto bg-gray-50 dark:bg-gray-800/50" data-id="element-5326">
                {menuItems.length === 0 ? <p className="text-sm text-gray-500 text-center py-2" data-id="element-5327">
                    No hay platos registrados
                  </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-5328">
                    {menuItems.map(item => <label key={item.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-5329">
                        <input type="checkbox" checked={formData.applicableMenuItems.includes(item.id)} onChange={() => toggleMenuItem(item.id)} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-5330" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-5331">
                          {item.name}
                        </span>
                      </label>)}
                  </div>}
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5332">
                {formData.applicableMenuItems.length === 0 ? 'Válido para TODOS los platos' : `Restringido a ${formData.applicableMenuItems.length} plato(s)`}
              </p>
            </div>
          </section>
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-b-2xl flex gap-3" data-id="element-5333">
          <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-5334">
            Cancelar
          </Button>
          <Button type="submit" fullWidth onClick={handleSubmit} data-id="element-5335">
            {promotion ? 'Actualizar' : 'Crear'} Promoción
          </Button>
        </div>
      </div>
    </div>;
}